'use client';

import { cn } from '@/lib/utils';

/**
 * Selection chrome overlay used by `AnnotationNode` when the node is
 * selected (and NOT in text-edit mode). Renders the Figjam / Confluence
 * affordance: a thin blue bounding-box border, four filled corner
 * squares, and a small tick at each edge midpoint to advertise the
 * resize gesture.
 *
 * The overlay is purely decorative — `pointer-events: none`. The actual
 * resize gesture is driven by the `ResizeEdge` invisible hit zones the
 * annotation node already mounts; the user clicks somewhere on the
 * edge / corner zone, the resize cursor flips on hover, and a drag
 * resizes. The squares are visual cues so the user understands WHY the
 * cursor is changing.
 *
 * Why not make the squares the literal hit targets:
 *   - The existing hit zones extend a few px outside the card edge,
 *     which feels precise without forcing pixel-perfect aim on a small
 *     decoration. Replacing them with the (smaller) squares would
 *     regress the touch / cursor experience.
 *   - The squares would have to be larger to be reliably clickable,
 *     which would compete visually with the connector dots that sit
 *     just outside them. Keeping the squares small + decorative
 *     preserves the figjam look without changing how a resize starts.
 */
export function AnnotationSelectionChrome({
  rounded = 0,
}: {
  /**
   * Border radius to match the host node's chrome. Current call sites:
   *   - sticky    → 0 (square paper rectangle)
   *   - text      → 4
   *   - rectangle → 6 (matches the shape primitive's `rx=6`)
   *   - other shapes (ellipse / diamond / triangle) → 0 (the bbox
   *     reads as selection, not as a second containing shape)
   */
  rounded?: number;
}) {
  // z-index 35 puts us BELOW the connector handles (40) but ABOVE the
  // resize hit zones (20–30). The corner squares overlap the corners
  // of the resize hit zones visually but stay click-through so the
  // resize zone underneath still receives the pointerdown.
  return (
    <div
      aria-hidden
      className="absolute -inset-px pointer-events-none z-[35]"
      style={{ borderRadius: rounded + 1 }}
    >
      <div
        className="absolute inset-0 border-2 border-blue-500 dark:border-blue-400"
        style={{ borderRadius: rounded }}
      />
      {/* Corner squares — Figjam-style filled blue handles. White
          outer border lifts them off coloured stickies; the dark
          theme branch swaps to a slate outline to maintain contrast. */}
      {(['tl', 'tr', 'bl', 'br'] as const).map((pos) => (
        <span
          key={pos}
          className={cn(
            'absolute h-2.5 w-2.5 bg-blue-500 dark:bg-blue-400',
            'rounded-[2px] border border-white dark:border-slate-900',
            'shadow-[0_1px_2px_rgba(15,23,42,0.18)]',
            pos === 'tl' && '-top-[5px] -left-[5px]',
            pos === 'tr' && '-top-[5px] -right-[5px]',
            pos === 'bl' && '-bottom-[5px] -left-[5px]',
            pos === 'br' && '-bottom-[5px] -right-[5px]',
          )}
        />
      ))}
      {/* Edge midpoint markers — short blue ticks centred on each
          side. Smaller than the corner squares so they read as "you
          can resize from this edge too" without competing with the
          connector dots that sit outside the box at the same
          midpoints. */}
      <span
        className="absolute left-1/2 -top-[3px] -translate-x-1/2 h-1.5 w-3 rounded-[1px] bg-blue-500 dark:bg-blue-400 border border-white dark:border-slate-900"
      />
      <span
        className="absolute left-1/2 -bottom-[3px] -translate-x-1/2 h-1.5 w-3 rounded-[1px] bg-blue-500 dark:bg-blue-400 border border-white dark:border-slate-900"
      />
      <span
        className="absolute top-1/2 -left-[3px] -translate-y-1/2 w-1.5 h-3 rounded-[1px] bg-blue-500 dark:bg-blue-400 border border-white dark:border-slate-900"
      />
      <span
        className="absolute top-1/2 -right-[3px] -translate-y-1/2 w-1.5 h-3 rounded-[1px] bg-blue-500 dark:bg-blue-400 border border-white dark:border-slate-900"
      />
    </div>
  );
}
